/* globals window */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { findDOMNode } from 'react-dom';
import { throttle, isNumber } from 'lodash';
import $ from 'jquery';
import displayName from 'component-lib/displayName';

import LoadingIndicator from '../../LoadingIndicator';

import styles from './style.scss';

const DEFAULT_BLOCK_SIZE = 50;
const ROW_BUFFER_SIZE = 2;

// Because of a Chrome/Windows bug we need to store the last two scroller values
//   to filter out the continously repeated patterns
let storedScrollerValues = [];

export default class InfiniteScrollHelper extends Component {
    static displayName = displayName('TableView/InfiniteScroll/Helper');

    static propTypes = {
        itemHeight: PropTypes.number.isRequired,
        results: PropTypes.array.isRequired,
        count: PropTypes.number.isRequired,
        onResultsNeeded: PropTypes.func.isRequired,
        loadingText: PropTypes.string.isRequired,
        resultComponent: PropTypes.func.isRequired,
        withScrollbar: PropTypes.bool,
        blockSize: PropTypes.number,
        showScrollbar: PropTypes.bool,
    };

    static defaultProps = {
        blockSize: DEFAULT_BLOCK_SIZE,
        withScrollbar: true,
        showScrollbar: true,
        tableProps: {},
    };

    constructor(props) {
        super(props);

        this.placeholdersRendered = [];
        this.isFetching = false;

        this.state = {
            start: 0,
            end: 0,
        };

        storedScrollerValues = [];
    }

    componentDidMount() {
        const $node = this.getTableHolder();
        let heightCheckInterval = null;

        this.throttledScroller = throttle(this.scroller, 100);
        $node.on('scroll', this.throttledScroller);

        const heightCheck = () => {
            if ($node.height() > 1) {
                window.clearInterval(heightCheckInterval);

                this.scroller();
                this.onRender();
            }
        };

        heightCheckInterval = window.setInterval(heightCheck, 100);
    }

    componentDidUpdate(prevProps) {
    // Comparing the old and new results count. If the value is false a new
    //   data package arrived so no more fetching.
    // We use undefined data for reserve the data spaces inside
    //   the results array whose aren't arrived yet. The filter in this case
    //   drop out the undefined values and comparing only the real data
    //   value's lengts.

        const currentResultsLength = this.props.results.filter(result => result).length;
        const prevResultsLength = prevProps.results.filter(result => result).length;

        if (
            currentResultsLength !== prevResultsLength ||
      (this.isFetching &&
        currentResultsLength === prevResultsLength &&
        !this.placeholdersRendered.length)
        ) {
            this.isFetching = false;
        }
        this.onRender();
    }

    componentWillUnmount() {
        this.getTableHolder().off('scroll', this.throttledScroller);
    }

    onRender() {
        if (this.placeholdersRendered.length > 0 && !this.isFetching) {
            const { blockSize, count, onResultsNeeded } = this.props;
            const firstRequestedElem = Math.min(...this.placeholdersRendered);
            const requestedRangeIndex = parseInt(firstRequestedElem / blockSize, 10);

            const min = requestedRangeIndex * blockSize;
            const max = Math.min(min + blockSize, count);

            this.isFetching = true;
            onResultsNeeded(min, max);
        }
        this.scroller();
    }

    // TODO: remove dependency on findDOMNode
    /* eslint-disable react/no-find-dom-node */
    getTableHolder() {
        return $(findDOMNode(this.tableHolder));
    }
    /* eslint-enable react/no-find-dom-node */

    getChildren({ placeholder }) {
        const { results, resultComponent, itemHeight } = this.props;
        const { start, end } = this.state;

        this.placeholdersRendered = [];

        const children = [];
        let i = start;

        while (i <= end) {
            const result = results[i];

            if (result) {
                children.push(resultComponent(result, i));
            } else {
                this.placeholdersRendered.push(i);
                children.push(React.createElement(placeholder, {
                    height: itemHeight,
                    key: i,
                    index: i,
                }),);
            }
            i += 1;
        }

        return children;
    }

    scroller = () => {
        const { count, itemHeight } = this.props;
        const { start: blockStart, end: blockEnd } = this.state;
        const $node = this.getTableHolder();
        const height = $node.height();
        const scrollTop = $node.scrollTop();

        let start = Math.max(Math.floor(scrollTop / itemHeight) - ROW_BUFFER_SIZE, 0);
        let end = Math.min(Math.ceil((scrollTop + height) / itemHeight) + ROW_BUFFER_SIZE, count);

        start = Math.max(start, 0);
        end = Math.min(end, count - 1);

        if (
            isNumber(start) &&
      isNumber(end) &&
      isNumber(blockStart) &&
      isNumber(blockEnd) &&
      (blockStart !== start || blockEnd !== end) &&
      !storedScrollerValues.some(elem => blockEnd === elem.blockEnd && end === elem.end)
        ) {
            if (this.state.start !== start || this.state.end !== end) {
                this.setState({ start, end });
            }

            storedScrollerValues.push({ blockEnd, end });
            storedScrollerValues = storedScrollerValues.slice(-2);
        } else {
            storedScrollerValues.shift();
        }
    };

    calculatePusherHeights() {
        const { count, itemHeight } = this.props;
        const { start, end } = this.state;

        const topHeight = Math.max(start, 0) * itemHeight;
        const bottomHeight = Math.max(count - 1 - end, 0) * itemHeight;

        return { topHeight, bottomHeight };
    }

    /* eslint-disable class-methods-use-this */
    renderScroll() {
        throw new Error('InfiniteScrollHelper.renderScroll method needs to be extended');
    }
    /* eslint-enable class-methods-use-this */

    render() {
        const { loadingText, withScrollbar, showScrollbar } = this.props;

        const content = this.renderScroll();
        const isLoading = this.placeholdersRendered.length > 0;

        const innerClass = `infinite-scroll-inner${isLoading ? '-loading' : ''}`;

        let scrollbarStyle = 'with-no-scrollbar';
        if (showScrollbar) {
            scrollbarStyle = 'with-custom-scrollbar';
            if (withScrollbar) {
                scrollbarStyle = '';
            }
        }

        const innerClassName = `infinite-scroll-inner ${scrollbarStyle}`;

        return (
            <div className={`infinite-scroll ${styles['infinite-scroll']}`}>
                <div
                    ref={(c) => {
                        this.tableHolder = c;
                    }}
                    className={`${innerClassName} ${styles[innerClass]}`}
                >
                    {content}
                </div>
                <LoadingIndicator text={loadingText} orientation="middle" isLoading={isLoading} />
            </div>
        );
    }
}
